import React, { useMemo, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import Button from '../../global/ui/Button';
import useResponsiveFontSize from '../SignUp/useResponsiveFontSize';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import { showToast } from '../../utils';

interface AgentStripeDepositProps {
  clientSecret: string;
  paymentIntentId: string;
  amount: string;
  currency: string;
  onSuccess: (paymentIntentId: string) => void;
}

function useStripeElementOptions() {
  const fontSize = useResponsiveFontSize();
  return useMemo(
    () => ({
      style: {
        base: {
          fontSize,
          fontWeight: 700,
          color: '#2F2F2F',
          fontFamily: 'sans-serif',
          '::placeholder': {
            color: 'grey',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    }),
    [fontSize],
  );
}

export default function AgentStripeDeposit({
  clientSecret,
  paymentIntentId,
  amount,
  currency,
  onSuccess,
}: AgentStripeDepositProps) {
  const stripe = useStripe();
  const elements = useElements();
  const options = useStripeElementOptions();
  const [loading, setLoading] = useState(false);
  const [formComplete, setFormComplete] = useState({
    card_number: false,
    card_expiry: false,
    card_cvc: false,
  });

  const formattedAmount = Number(amount).toFixed(2);
  const isReady = formComplete.card_number && formComplete.card_expiry && formComplete.card_cvc;

  const handlePay = async () => {
    if (!stripe || !elements || !isReady) {
      showToast(STRING_CONSTANTS.please_correct, 'error');
      return;
    }
    const card = elements.getElement(CardNumberElement);
    if (!card) {
      return;
    }

    setLoading(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card,
        },
      });
      if (error) {
        throw error;
      }
      if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent.id || paymentIntentId);
        return;
      }
      throw new Error('Payment was not completed.');
    } catch (err: any) {
      showToast(err?.message || 'Payment failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <div>
        <label>
          Card number
          <CardNumberElement
            options={options}
            onChange={(event) => setFormComplete((prev) => ({ ...prev, card_number: event.complete }))}
          />
        </label>
        <label>
          CVC
          <CardCvcElement
            options={options}
            onChange={(event) => setFormComplete((prev) => ({ ...prev, card_cvc: event.complete }))}
          />
        </label>
        <label>
          Expiration date
          <CardExpiryElement
            options={options}
            onChange={(event) => setFormComplete((prev) => ({ ...prev, card_expiry: event.complete }))}
          />
        </label>
      </div>

      <div className="bottom-wrapper">
        <div className="all-totals">
          <div className="total-dipst">
            <div>
              <img
                src="/assets/images/question.svg"
                alt=""
                style={{ visibility: 'hidden' }}
              />
              Total Deposit
            </div>
            <div>
              {currency} {formattedAmount}
            </div>
          </div>
        </div>

        <Button
          type="button"
          btnTxt="Pay deposit"
          className="error continue-btn Pay-btn"
          isLoading={loading}
          onClick={handlePay}
          disabled={!isReady || loading}
        />
      </div>

      {loading && (
        <div className="pay-popup-outer">
          <div className="overlay" />
          <div className="pay-popup">
            <div className="gif-img">
              <img src="/assets/images/loading.gif" alt="" />
            </div>
            Please wait, processing your deposit.
          </div>
        </div>
      )}
    </form>
  );
}
